import { useEffect, useRef, useState } from "react";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { Dialog } from "@/components/ui/Dialog";
import { TextInput } from "@/components/ui/Field";
import { HtmlNotesEditor } from "@/components/ui/HtmlNotesEditor";
import { Spinner } from "@/components/ui/Spinner";
import type { LockedAutocompleteHandle } from "@/components/LockedAutocomplete";
import { LieferantSearchField } from "@/components/LieferantSearchField";
import { ApiError } from "@/lib/apiClient";
import { toast } from "@/lib/toast";
import {
  createArtikel,
  getArtikelStammdatenVorschlaege,
  type ArtikelCreatePayload,
} from "@/lib/artikel";
import {
  getRabattRegeln,
  matchRabattRegel,
  rabattStateFromRegeln,
  type ArtikelRabattState,
  type RabattRegel,
} from "@/lib/artikelRabatt";
import {
  getZuschlagRegeln,
  matchZuschlagRegel,
  roundMoney,
  zuschlagStateFromRegeln,
  type ArtikelZuschlagState,
  type ZuschlagRegel,
} from "@/lib/artikelZuschlag";
import {
  ArtikelRabattSection,
  previewEk as previewRabattEk,
  previewRabattPct,
  rabattSelectionToSaveBody,
  type RabattSelection,
} from "@/pages/artikel/ArtikelRabattSection";
import {
  ArtikelZuschlagSection,
  selectionToSaveBody,
  type ZuschlagSelection,
} from "@/pages/artikel/ArtikelZuschlagSection";
import {
  INACTIVE_RABATT,
  INACTIVE_ZUSCHLAG,
  activateRabatt,
  activateZuschlag,
  isRabattActive,
  isZuschlagActive,
} from "@/pages/artikel/artikelPriceSelection";
import { ArtikelSuggestField } from "@/pages/artikel/ArtikelSuggestField";
import { UnitAutocompleteField } from "@/pages/dokumente/UnitAutocompleteField";

function parseNum(raw: string): number | undefined {
  const s = raw.trim().replace(/\s/g, "").replace(",", ".");
  if (!s) return undefined;
  const n = Number(s);
  return Number.isFinite(n) ? n : undefined;
}

function pendingOrValue(handle: LockedAutocompleteHandle | null, committed: string): string {
  return (handle?.getPendingQuery() || committed).trim();
}

function notesPlainText(html: string): string {
  return html
    .replace(/<br\s*\/?>/gi, "")
    .replace(/&nbsp;/gi, " ")
    .replace(/<[^>]+>/g, "")
    .trim();
}

const EMPTY_FORM = {
  heroNr: "",
  name: "",
  beschreibung: "",
  einheit: "",
  ean: "",
  matchcode: "",
  hersteller: "",
  herstellerNr: "",
  lieferantenNr: "",
  warengruppe: "",
  lieferantName: "",
  preisBrutto: "",
  rabatt: "",
  preisNetto: "",
  preismenge: "",
  preisEinheit: "",
  preisGueltigBis: "",
};

const EMPTY_ZUSCHLAG: ZuschlagSelection = INACTIVE_ZUSCHLAG;

export function ArtikelCreateDialog({
  open,
  onClose,
  onCreated,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (id: number) => void;
}) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [lieferantGpId, setLieferantGpId] = useState<number | null>(null);
  const [regeln, setRegeln] = useState<ZuschlagRegel[]>([]);
  const [zuschlagState, setZuschlagState] = useState<ArtikelZuschlagState | null>(null);
  const [zuschlagSelection, setZuschlagSelection] = useState<ZuschlagSelection>(EMPTY_ZUSCHLAG);
  const [zuschlagTouched, setZuschlagTouched] = useState(false);
  const [rabattRegeln, setRabattRegeln] = useState<RabattRegel[]>([]);
  const [rabattState, setRabattState] = useState<ArtikelRabattState | null>(null);
  const [rabattSelection, setRabattSelection] = useState<RabattSelection>(INACTIVE_RABATT);
  const [rabattTouched, setRabattTouched] = useState(false);
  const [matchcodes, setMatchcodes] = useState<string[]>([]);
  const [kategorien, setKategorien] = useState<string[]>([]);
  const [importGeschuetzt, setImportGeschuetzt] = useState(false);
  const [preisGeschuetzt, setPreisGeschuetzt] = useState(false);
  const [interneNotizen, setInterneNotizen] = useState("");
  const matchcodeRef = useRef<LockedAutocompleteHandle>(null);
  const kategorieRef = useRef<LockedAutocompleteHandle>(null);

  useEffect(() => {
    if (!open) return;
    setForm(EMPTY_FORM);
    setLieferantGpId(null);
    setError(null);
    setImportGeschuetzt(false);
    setPreisGeschuetzt(false);
    setInterneNotizen("");
    setZuschlagSelection(EMPTY_ZUSCHLAG);
    setZuschlagTouched(false);
    setRabattSelection(INACTIVE_RABATT);
    setRabattTouched(false);
    let cancelled = false;
    void getZuschlagRegeln()
      .then((nextRegeln) => {
        if (cancelled) return;
        setRegeln(nextRegeln);
        setZuschlagState(zuschlagStateFromRegeln(nextRegeln));
      })
      .catch(() => {
        if (cancelled) return;
        setRegeln([]);
        setZuschlagState(zuschlagStateFromRegeln([]));
      });
    void getRabattRegeln()
      .then((nextRegeln) => {
        if (cancelled) return;
        setRabattRegeln(nextRegeln);
        setRabattState(rabattStateFromRegeln(nextRegeln));
      })
      .catch(() => {
        if (cancelled) return;
        setRabattRegeln([]);
        setRabattState(rabattStateFromRegeln([]));
      });
    void getArtikelStammdatenVorschlaege()
      .then((vorschlaege) => {
        if (cancelled) return;
        setMatchcodes(vorschlaege.matchcodes);
        setKategorien(vorschlaege.kategorien);
      })
      .catch(() => {
        if (cancelled) return;
        setMatchcodes([]);
        setKategorien([]);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  const ekNetto = parseNum(form.preisNetto);
  const ekBrutto = parseNum(form.preisBrutto);
  const listenpreis = ekBrutto != null && ekBrutto > 0 ? ekBrutto : null;
  const ekRabatt = parseNum(form.rabatt);
  const rabattPreviewEk = previewRabattEk(
    rabattSelection,
    listenpreis,
    rabattState?.staffeln ?? [],
  );
  const rabattPreviewPct = previewRabattPct(rabattSelection, rabattState?.staffeln ?? []);
  const ek =
    rabattPreviewEk ??
    ekNetto ??
    (ekBrutto != null ? roundMoney(ekBrutto * (1 - (ekRabatt ?? 0) / 100)) : null);
  const hasPreisDaten =
    ek != null ||
    ekBrutto != null ||
    ekNetto != null ||
    Boolean(form.preisEinheit.trim()) ||
    Boolean(form.preisGueltigBis.trim()) ||
    parseNum(form.preismenge) != null;

  useEffect(() => {
    if (rabattTouched || zuschlagTouched) return;
    const rabattMatch = matchRabattRegel(listenpreis, rabattRegeln);
    if (rabattMatch?.id != null) {
      setRabattSelection({ modus: "regel", regel_id: rabattMatch.id });
      setZuschlagSelection(INACTIVE_ZUSCHLAG);
      return;
    }
    const zuschlagMatch = matchZuschlagRegel(ek, regeln);
    if (zuschlagMatch?.id != null) {
      setZuschlagSelection({ modus: "regel", regel_id: zuschlagMatch.id });
      setRabattSelection(INACTIVE_RABATT);
      return;
    }
    setRabattSelection(INACTIVE_RABATT);
    setZuschlagSelection(INACTIVE_ZUSCHLAG);
  }, [listenpreis, rabattRegeln, ek, regeln, rabattTouched, zuschlagTouched]);

  function set<K extends keyof typeof EMPTY_FORM>(key: K, value: string) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  function handleZuschlagChange(next: ZuschlagSelection) {
    setZuschlagTouched(true);
    setRabattTouched(true);
    const exclusive = activateZuschlag(next);
    setRabattSelection(exclusive.rabatt);
    setZuschlagSelection(exclusive.zuschlag);
  }

  function handleRabattChange(next: RabattSelection) {
    setRabattTouched(true);
    setZuschlagTouched(true);
    const exclusive = activateRabatt(next);
    setRabattSelection(exclusive.rabatt);
    setZuschlagSelection(exclusive.zuschlag);
    const pct = previewRabattPct(next, rabattState?.staffeln ?? []);
    const nextEk = previewRabattEk(next, listenpreis, rabattState?.staffeln ?? []);
    setForm((f) => ({
      ...f,
      rabatt: pct != null ? String(pct) : f.rabatt,
      preisNetto: nextEk != null ? String(nextEk) : f.preisNetto,
    }));
  }

  async function handleSave() {
    if (!form.heroNr.trim()) {
      setError("Bitte eine Artikelnummer eingeben.");
      return;
    }
    if (!form.name.trim()) {
      setError("Bitte einen Namen eingeben.");
      return;
    }

    const matchcode = pendingOrValue(matchcodeRef.current, form.matchcode);
    const warengruppe = pendingOrValue(kategorieRef.current, form.warengruppe);

    let rabatt_auswahl: ArtikelCreatePayload["rabatt_auswahl"];
    let zuschlag: ArtikelCreatePayload["zuschlag"];
    if (isRabattActive(rabattSelection)) {
      const rabattBody = rabattSelectionToSaveBody(rabattSelection);
      if ("error" in rabattBody) {
        setError(rabattBody.error);
        return;
      }
      rabatt_auswahl = rabattBody;
    } else if (isZuschlagActive(zuschlagSelection)) {
      if (zuschlagSelection.modus === "regel" && ek != null && ek > 0) {
        zuschlag = { modus: "regel", regel_id: zuschlagSelection.regel_id };
      } else if (zuschlagSelection.modus === "manuell_prozent") {
        const bodyOrError = selectionToSaveBody(zuschlagSelection);
        if ("error" in bodyOrError) {
          setError(bodyOrError.error);
          return;
        }
        zuschlag = bodyOrError;
      } else if (
        zuschlagSelection.modus === "manuell_vk" &&
        zuschlagSelection.verkaufspreis != null
      ) {
        const bodyOrError = selectionToSaveBody(zuschlagSelection);
        if ("error" in bodyOrError) {
          setError(bodyOrError.error);
          return;
        }
        zuschlag = bodyOrError;
      }
    }

    setSaving(true);
    setError(null);
    const notesHtml = notesPlainText(interneNotizen) ? interneNotizen : undefined;
    const payload: ArtikelCreatePayload = {
      hero_nr: form.heroNr.trim(),
      name: form.name.trim(),
      beschreibung: form.beschreibung.trim() || undefined,
      einheit: form.einheit.trim() || undefined,
      ean: form.ean.trim() || undefined,
      matchcode: matchcode || undefined,
      hersteller: form.hersteller.trim() || undefined,
      hersteller_nr: form.herstellerNr.trim() || undefined,
      lieferanten_nr: form.lieferantenNr.trim() || undefined,
      warengruppe: warengruppe || undefined,
      lieferant_name: form.lieferantName.trim() || undefined,
      lieferant_geschaeftspartner_id: lieferantGpId,
      preis_brutto: parseNum(form.preisBrutto),
      rabatt: parseNum(form.rabatt),
      preis_netto: ek ?? undefined,
      preismenge:
        parseNum(form.preismenge) != null
          ? Math.round(parseNum(form.preismenge)!)
          : undefined,
      preis_einheit: form.preisEinheit.trim() || undefined,
      preis_gueltig_bis: form.preisGueltigBis.trim() || undefined,
      zuschlag,
      rabatt_auswahl,
      import_geschuetzt: importGeschuetzt || undefined,
      preis_geschuetzt: (hasPreisDaten && preisGeschuetzt) || undefined,
      interne_notizen: notesHtml,
    };
    try {
      const result = await createArtikel(payload);
      toast.success("Artikel angelegt.");
      onCreated(result.id);
      onClose();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Anlegen fehlgeschlagen.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title="Artikel anlegen"
      size="2xl"
      bodyClassName="overflow-y-auto max-h-[75vh]"
      dirty={
        JSON.stringify(form) !== JSON.stringify(EMPTY_FORM) ||
        importGeschuetzt ||
        preisGeschuetzt ||
        Boolean(notesPlainText(interneNotizen))
      }
    >
      <div className="space-y-4">
        {error ? <Alert variant="error">{error}</Alert> : null}

        <section className="space-y-3">
          <h3 className="text-sm font-semibold text-label">Stammdaten</h3>
          <div className="grid gap-3 sm:grid-cols-2">
            <TextInput
              label="Artikelnummer *"
              value={form.heroNr}
              onChange={(e) => set("heroNr", e.target.value)}
              hint="Muss eindeutig sein – doppelte Artikelnummern werden abgelehnt."
            />
            <TextInput label="Name *" value={form.name} onChange={(e) => set("name", e.target.value)} />
            <UnitAutocompleteField
              label="Einheit"
              value={form.einheit}
              onChange={(value) => set("einheit", value)}
            />
            <TextInput label="EAN" value={form.ean} onChange={(e) => set("ean", e.target.value)} />
            <ArtikelSuggestField
              label="Matchcode"
              value={form.matchcode}
              onChange={(value) => set("matchcode", value)}
              suggestions={matchcodes}
              fieldRef={matchcodeRef}
            />
            <ArtikelSuggestField
              label="Kategorie"
              value={form.warengruppe}
              onChange={(value) => set("warengruppe", value)}
              suggestions={kategorien}
              fieldRef={kategorieRef}
            />
            <TextInput label="Hersteller" value={form.hersteller} onChange={(e) => set("hersteller", e.target.value)} />
            <TextInput label="Hersteller-Nr." value={form.herstellerNr} onChange={(e) => set("herstellerNr", e.target.value)} />
          </div>
          <label className="block text-sm">
            <span className="mb-1 block font-medium text-label">Beschreibung</span>
            <textarea
              className="min-h-[80px] w-full rounded-ios border border-separator bg-card px-3 py-2 text-sm"
              value={form.beschreibung}
              onChange={(e) => set("beschreibung", e.target.value)}
            />
          </label>
          <div className="grid gap-3 sm:grid-cols-2">
            <LieferantSearchField
              label="Lieferant"
              value={form.lieferantName}
              selectedGpId={lieferantGpId}
              onChange={({ name, gpId }) => {
                set("lieferantName", name);
                setLieferantGpId(gpId);
              }}
            />
            <TextInput
              label="Lieferanten-Nr."
              value={form.lieferantenNr}
              onChange={(e) => set("lieferantenNr", e.target.value)}
              hint="Artikelnummer beim Lieferanten. Unabhängig von der Hersteller-Nr."
            />
          </div>
        </section>

        <section className="rounded-ios border border-separator bg-card-elevated px-4 py-3">
          <h3 className="text-sm font-semibold text-label">Import-Schutz</h3>
          <p className="mt-1 text-xs text-label-secondary">
            Geschützte Artikel bzw. Preise werden bei Datanorm- und Excel-Importen nicht
            überschrieben (z.&nbsp;B. bei ausgehandelten Sonderkonditionen).
          </p>
          <div className="mt-2 flex flex-wrap gap-4">
            <label className="flex items-center gap-2 text-sm text-label">
              <input
                type="checkbox"
                className="rounded border-separator"
                checked={importGeschuetzt}
                disabled={saving}
                onChange={(e) => setImportGeschuetzt(e.target.checked)}
              />
              Ganzen Artikel schützen
            </label>
            <label
              className={`flex items-center gap-2 text-sm text-label${hasPreisDaten ? "" : " opacity-50"}`}
            >
              <input
                type="checkbox"
                className="rounded border-separator"
                checked={preisGeschuetzt}
                disabled={saving || !hasPreisDaten}
                onChange={(e) => setPreisGeschuetzt(e.target.checked)}
              />
              Nur aktuellen Preis schützen
            </label>
          </div>
        </section>

        <section className="rounded-ios border border-separator bg-card-elevated px-4 py-3">
          <h3 className="text-sm font-semibold text-label">Interne Notizen</h3>
          <p className="mt-1 text-xs text-label-secondary">
            Nur für das Büro sichtbar. Diese Notizen werden bei Datanorm- und Excel-Importen
            nicht verändert.
          </p>
          <HtmlNotesEditor
            className="mt-3"
            minRows={4}
            placeholder="Interne Hinweise, Lagerort, Besonderheiten …"
            value={interneNotizen}
            onChange={setInterneNotizen}
            disabled={saving}
          />
        </section>

        <section className="space-y-3 border-t border-separator pt-4">
          <h3 className="text-sm font-semibold text-label">Preise (optional)</h3>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            <TextInput label="Bruttopreis (EK)" value={form.preisBrutto} onChange={(e) => set("preisBrutto", e.target.value)} />
            <TextInput label="Rabatt %" value={form.rabatt} onChange={(e) => set("rabatt", e.target.value)} />
            <TextInput label="Nettopreis (EK)" value={form.preisNetto} onChange={(e) => set("preisNetto", e.target.value)} />
            <TextInput label="Preismenge" value={form.preismenge} onChange={(e) => set("preismenge", e.target.value)} />
            <UnitAutocompleteField
              label="Preiseinheit"
              value={form.preisEinheit}
              onChange={(value) => set("preisEinheit", value)}
            />
            <TextInput
              label="Gültig bis"
              type="date"
              value={form.preisGueltigBis}
              onChange={(e) => set("preisGueltigBis", e.target.value)}
              hint="Leer = unbegrenzt gültig."
            />
          </div>
        </section>

        <div className="space-y-6 border-t border-separator pt-4">
          <ArtikelRabattSection
            state={rabattState}
            selection={rabattSelection}
            onChange={handleRabattChange}
            disabled={saving}
            listenpreisOverride={listenpreis}
          />
          <ArtikelZuschlagSection
            state={zuschlagState}
            selection={zuschlagSelection}
            onChange={handleZuschlagChange}
            disabled={saving}
            ekOverride={ek}
            listenpreis={listenpreis}
            rabatt={rabattPreviewPct ?? parseNum(form.rabatt) ?? null}
          />
        </div>

        <div className="flex justify-end gap-2 border-t border-separator pt-4">
          <Button variant="secondary" onClick={onClose} disabled={saving}>
            Abbrechen
          </Button>
          <Button onClick={() => void handleSave()} disabled={saving}>
            {saving ? <Spinner className="h-4 w-4" /> : "Anlegen"}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
